/** 한국(Asia/Seoul) 달력일 기준 — 결제·만료일 안내와 동일하게 맞춤 */
export const BILLING_NUDGE_TZ = 'Asia/Seoul'

export const BILLING_PAYWALL_MODAL_LAST_DATE_KEY = 'sgez.billingPaywall.modalLastDateKst'

export function dateStringInTimeZone(date, timeZone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** @param {string|null|undefined} isoInstant */
export function dateStringForInstantInTimeZone(isoInstant, timeZone) {
  if (!isoInstant) return null
  const d = new Date(isoInstant)
  if (Number.isNaN(d.getTime())) return null
  return dateStringInTimeZone(d, timeZone)
}

/**
 * 달력일 기준: endYmd - todayYmd (정수 일 수).
 * @param {string} todayYmd `YYYY-MM-DD`
 * @param {string} endYmd `YYYY-MM-DD`
 */
export function calendarDaysFromTodayToEndInclusive(todayYmd, endYmd) {
  if (!todayYmd || !endYmd) return null
  const t = new Date(`${todayYmd}T12:00:00`)
  const e = new Date(`${endYmd}T12:00:00`)
  if (Number.isNaN(t.getTime()) || Number.isNaN(e.getTime())) return null
  return Math.round((e.getTime() - t.getTime()) / 86400000)
}

/**
 * 조직이 구독료를 부담하는 경우 개인 결제 유도 배너/모달 제외
 * @param {Record<string, unknown>|null|undefined} orgContract `/billing/me` 의 orgContract
 */
export function isOrgPaysCurrentlyEffective(orgContract) {
  if (!orgContract || orgContract.currentlyEffective !== true) return false
  const bm = String(orgContract.billingMode || orgContract.billingModel || '').toUpperCase()
  return bm === 'ORG_PAYS'
}

/**
 * @typedef {'none' | 'warn7' | 'warn4' | 'warn1' | 'lastDay' | 'expired' | 'suspended'} BillingPaywallNudgeLevel
 * @typedef {{ level: BillingPaywallNudgeLevel, daysLeft: number|null, endsAtYmd: string|null }} BillingPaywallNudge
 */

/**
 * @param {{ subscription?: Record<string, unknown>|null, orgContract?: Record<string, unknown>|null }} billingMe
 * @returns {BillingPaywallNudge}
 */
export function computeBillingPaywallNudge(billingMe) {
  if (!billingMe || typeof billingMe !== 'object') {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  if (isOrgPaysCurrentlyEffective(billingMe.orgContract)) {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  const subscription = billingMe.subscription && typeof billingMe.subscription === 'object'
    ? billingMe.subscription
    : null
  if (!subscription) {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  const status = String(subscription.status || '').toUpperCase()
  if (status === 'TRIAL') {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  if (status === 'SUSPENDED') {
    return { level: 'suspended', daysLeft: null, endsAtYmd: dateStringForInstantInTimeZone(/** @type {string} */ (subscription.endsAt), BILLING_NUDGE_TZ) }
  }
  if (status === 'EXPIRED') {
    return { level: 'expired', daysLeft: null, endsAtYmd: dateStringForInstantInTimeZone(/** @type {string} */ (subscription.endsAt), BILLING_NUDGE_TZ) }
  }

  const endsAt = subscription.endsAt ? String(subscription.endsAt) : null
  const endsAtYmd = dateStringForInstantInTimeZone(endsAt, BILLING_NUDGE_TZ)
  const todayYmd = dateStringInTimeZone(new Date(), BILLING_NUDGE_TZ)
  if (!endsAtYmd || !todayYmd) {
    return { level: 'none', daysLeft: null, endsAtYmd }
  }
  const daysLeft = calendarDaysFromTodayToEndInclusive(todayYmd, endsAtYmd)
  if (daysLeft === null) {
    return { level: 'none', daysLeft: null, endsAtYmd }
  }
  if (daysLeft < 0) {
    return { level: 'expired', daysLeft, endsAtYmd }
  }
  if (daysLeft === 0) {
    return { level: 'lastDay', daysLeft: 0, endsAtYmd }
  }
  if (daysLeft === 1) return { level: 'warn1', daysLeft: 1, endsAtYmd }
  if (daysLeft === 4) return { level: 'warn4', daysLeft: 4, endsAtYmd }
  if (daysLeft === 7) return { level: 'warn7', daysLeft: 7, endsAtYmd }
  return { level: 'none', daysLeft, endsAtYmd }
}

/** @param {BillingPaywallNudgeLevel} level */
export function billingPaywallNudgeNeedsUi(level) {
  return level !== 'none'
}
